import React, { memo, useCallback, useEffect, useRef } from "react";
import {
  TextAreaInput,
  TextAreaInputHandle
} from "../../../../Forms/Inputs/TextArea/React";
import InputWrapperWithHeading from "../shared/inputWrapperWithHeading";

interface ValidatedTextAreaFieldProps {
  id: string;
  name?: string;
  label?: string;
  description?: string;
  errorMessage?: string;
  placeholder?: string;
  value?: string;
  required?: boolean;
  isValid: boolean;
  hasBlurred: boolean;
  formValidated: boolean;
  forwardedRef?: React.RefObject<TextAreaInputHandle>;
  onValueChange: (value: string) => void;
  onBlur?: () => void;
}

/**
 * ValidatedTextAreaField is a reusable form field that combines InputWrapperWithHeading
 * and TextAreaInput with built-in validation logic.
 */
export const ValidatedTextAreaField = memo(function ValidatedTextAreaField({
  id,
  name,
  label,
  description,
  errorMessage,
  placeholder,
  value,
  required = false,
  isValid,
  hasBlurred,
  formValidated,
  forwardedRef,
  onValueChange,
  onBlur
}: ValidatedTextAreaFieldProps) {
  const valueRef = useRef(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const handleValueChanged = useCallback(
    (newValue: string) => {
      onValueChange(newValue);
    },
    [onValueChange]
  );

  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur();
    }
  }, [onBlur]);

  const showError = !isValid && (formValidated || hasBlurred);

  return (
    <InputWrapperWithHeading
      id={id}
      label={label}
      description={description}
      errorMessage={errorMessage}
      required={required}
      invalid={showError}
    >
      <TextAreaInput
        ref={forwardedRef}
        id={id}
        name={name}
        placeholder={placeholder}
        value={value}
        onValueChanged={handleValueChanged}
        onBlur={handleBlur}
        invalid={showError}
      />
    </InputWrapperWithHeading>
  );
});

export default ValidatedTextAreaField;
